import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Copyable,
  DataTable,
  DataTableColumnHeader,
  Input,
  PrimaryFullScreenSpinner,
  TabLink,
  TabLinkContainer,
  toast,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import Decimal from "decimal.js";
import { Suspense, useMemo, useState } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  CalastonePastTransactionFragment,
  CalastoneTransactionRowFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const tabs = ["queue", "past"] as const;
type Tab = (typeof tabs)[number];

export const Route = createFileRoute("/__auth/calastone-pricing-queue/$tab")({
  head: () => opsHead("Calastone Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const params = useParams({ strict: false });

  let selectedTab: Tab | undefined;
  if (tabs.includes(params["tab"] as Tab)) {
    selectedTab = params["tab"] as Tab;
  } else {
    throw new NotFoundError();
  }

  return (
    <AdminPageContainer>
      <AdminPageHeader title="Calastone Queue" />
      <TabLinkContainer className="mb-6">
        <TabLink title="Pricing Queue" to="/calastone-pricing-queue/queue" />
        <TabLink title="Past Transactions" to="/calastone-pricing-queue/past" />
      </TabLinkContainer>
      {selectedTab === "queue" ? <QueueTab /> : <PastTab />}
    </AdminPageContainer>
  );
}

function formatSettlementDate(date: Date | string | null | undefined): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function QueueTab() {
  const { data, refetch, loading } = useQuery(
    graphql(`
      query CalastonePricingQueue {
        calastoneTransactions(first: 200, filter: { statuses: [TO_PROCESS] }) {
          calastoneTransactions {
            id
            ...CalastoneTransactionRow
          }
        }
      }

      fragment CalastoneTransactionRow on CalastoneTransaction {
        id
        dealRef
        totalUnits
        status
        settlementDate
        expectedUnitPrice
      }
    `),
    { notifyOnNetworkStatusChange: true },
  );

  const transactions = data?.calastoneTransactions?.calastoneTransactions ?? [];
  const onSuccess = useMemo(() => () => void refetch(), [refetch]);

  const columns = useMemo<ColumnDef<CalastoneTransactionRowFragment>[]>(
    () => [
      {
        id: "id",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="ID" />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-neutral-500">
            <Copyable copyText={row.original.id}>
              #{row.original.id.slice(-8)}
            </Copyable>
          </span>
        ),
      },
      {
        id: "dealRef",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Deal Ref" />
        ),
        accessorFn: (row) => row.dealRef,
      },
      {
        id: "settlementDate",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Settlement Date" />
        ),
        cell: ({ row }) => formatSettlementDate(row.original.settlementDate),
      },
      {
        id: "totalUnits",
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Total Units" />
        ),
        cell: ({ row }) =>
          row.original.totalUnits != null
            ? String(row.original.totalUnits)
            : "-",
      },
      {
        id: "actions",
        enableSorting: false,
        header: () => null,
        cell: ({ row }) => (
          <QueueActionsCell txn={row.original} onSuccess={onSuccess} />
        ),
      },
    ],
    [onSuccess],
  );

  if (loading) {
    return <PrimaryFullScreenSpinner />;
  }

  return (
    <DataTable
      data={transactions}
      columns={columns}
      isLoadingData={false}
      manualPagination={true}
      manualSorting={true}
      hidePagination={true}
      pageSize={200}
      className="overflow-hidden border border-zinc-200"
    />
  );
}

function QueueActionsCell({
  txn,
  onSuccess,
}: {
  txn: CalastoneTransactionRowFragment;
  onSuccess: () => void;
}) {
  const [unitPrice, setUnitPrice] = useState("");

  const [setCalastoneTransactionUnitPrice, { loading: submitLoading }] =
    useMutation(
      graphql(`
        mutation SetCalastoneTransactionUnitPrice($input: SetCalastoneTransactionUnitPriceInput!) {
          setCalastoneTransactionUnitPrice(input: $input) {
            calastoneTransaction {
              id
              status
            }
          }
        }
      `),
    );

  const [voidCalastoneTransaction, { loading: voidLoading }] = useMutation(
    graphql(`
      mutation VoidCalastoneTransaction($input: VoidCalastoneTransactionInput!) {
        voidCalastoneTransaction(input: $input) {
          calastoneTransaction {
            id
            status
          }
        }
      }
    `),
  );

  async function handleSubmit() {
    if (!unitPrice.trim()) return;
    await setCalastoneTransactionUnitPrice({
      variables: { input: { id: txn.id, unitPrice: new Decimal(unitPrice) } },
    });
    toast({ kind: "success", title: "Unit price set", autoHide: true });
    onSuccess();
  }

  async function handleVoid() {
    await voidCalastoneTransaction({
      variables: { input: { id: txn.id } },
    });
    toast({ kind: "success", title: "Transaction voided", autoHide: true });
    onSuccess();
  }

  const isLoading = submitLoading || voidLoading;

  const priceDeviation = (() => {
    if (!unitPrice.trim() || !txn.expectedUnitPrice) return null;
    const entered = new Decimal(unitPrice);
    const expected = new Decimal(txn.expectedUnitPrice);
    if (expected.isZero()) return null;
    return entered.minus(expected).div(expected).abs().times(100);
  })();

  const showPriceWarning = priceDeviation !== null && priceDeviation.gt(1);

  return (
    <div className="flex items-center justify-end gap-2">
      <div className="flex flex-col items-end gap-1">
        {txn.expectedUnitPrice && (
          <span className="text-neutral-400 text-xs">
            Latest unit price: {new Decimal(txn.expectedUnitPrice).toFixed(5)}
          </span>
        )}
        <Input
          type="number"
          step="any"
          placeholder="0.00000"
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
          className="w-36"
        />
        {showPriceWarning && (
          <span className="text-error-700 text-xs">
            {priceDeviation.toFixed(2)}% off expected
          </span>
        )}
      </div>
      <Button
        size="sm"
        onClick={() => void handleSubmit()}
        disabled={isLoading || !unitPrice.trim()}
        loading={submitLoading}
      >
        Submit
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => void handleVoid()}
        disabled={isLoading}
        loading={voidLoading}
      >
        Void
      </Button>
    </div>
  );
}

const pastColumns: ColumnDef<CalastonePastTransactionFragment>[] = [
  {
    id: "id",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => (
      <span className="font-mono text-neutral-500">
        <Copyable copyText={row.original.id}>
          #{row.original.id.slice(-8)}
        </Copyable>
      </span>
    ),
  },
  {
    id: "dealRef",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Deal Ref" />
    ),
    accessorFn: (row) => row.dealRef,
  },
  {
    id: "settlementDate",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Settlement Date" />
    ),
    cell: ({ row }) => formatSettlementDate(row.original.settlementDate),
  },
  {
    id: "totalUnits",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total Units" />
    ),
    cell: ({ row }) =>
      row.original.totalUnits != null ? String(row.original.totalUnits) : "-",
  },
  {
    id: "status",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <span className="text-neutral-600 capitalize">
        {row.original.status.toLowerCase().replace("_", " ")}
      </span>
    ),
  },
];

function PastTab() {
  const { data, fetchMore, loading } = useQuery(
    graphql(`
      query CalastonePastTransactions($after: String, $first: Int!) {
        calastoneTransactions(after: $after, first: $first, filter: { statuses: [PRICED, VOID, PROCESSED] }) {
          pageInfo {
            hasNextPage
            endCursor
          }
          calastoneTransactions {
            id
            ...CalastonePastTransaction
          }
        }
      }

      fragment CalastonePastTransaction on CalastoneTransaction {
        id
        dealRef
        totalUnits
        status
        settlementDate
      }
    `),
    {
      variables: { after: null, first: 20 },
      notifyOnNetworkStatusChange: true,
    },
  );

  const transactions = data?.calastoneTransactions?.calastoneTransactions ?? [];
  const pageInfo = data?.calastoneTransactions?.pageInfo;

  return (
    <div>
      <DataTable
        data={transactions}
        columns={pastColumns}
        isLoadingData={loading}
        manualPagination={true}
        manualSorting={true}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border border-zinc-200"
      />
      <div className="mt-4 flex w-full justify-center">
        {pageInfo?.hasNextPage ? (
          <Button
            variant="outline"
            onClick={() =>
              fetchMore({
                variables: { after: pageInfo.endCursor, first: 20 },
              })
            }
            loading={loading}
          >
            Load more
          </Button>
        ) : (
          transactions.length > 0 && (
            <p className="text-gray-500 text-sm">No more data</p>
          )
        )}
      </div>
    </div>
  );
}
